'use client';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sun,
  Moon,
  ArrowLeft,
  Edit,
  Share,
  Download,
  ChevronDown,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useThemeWaveToggle } from '@/hooks/useThemeWaveToggle';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { cn } from '@/lib/utils';
import { isAdminOrOwner } from '@/lib/rbac';
import { useAskEllieSidebarStore } from '@/state/askEllieSidebarState';
import { useHasMounted } from '@/hooks/useHasMounted';
import { PanelLeft, PanelRight } from 'lucide-react';
import { useChatSidebarCollapsed } from '@/hooks/useChatSidebarCollapsed';
import { useUserLogged } from '@/hooks/useUserLogged';
import dynamic from 'next/dynamic';

// Agent-insights only + pulls in the connect-CLI modal — load on demand so it
// stays out of every other dashboard route's bundle.
const MembersActivePill = dynamic(() =>
  import('@/app/(dashboard)/agent-insights/components/MembersActivePill').then(
    (m) => m.MembersActivePill
  )
);

interface HeaderProps {
  documentName?: string;
  showDocumentActions?: boolean;
}

export function Header({
  documentName = 'Document Name',
  showDocumentActions = true,
}: HeaderProps) {
  const { resolvedTheme } = useTheme();
  const { btnRef: themeBtnRef, toggleTheme } =
    useThemeWaveToggle<HTMLButtonElement>();
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useUserLogged();

  const isAIAssistantPage = pathname?.startsWith('/ask-ellie');
  // The pathname prefix also matches the member's own page
  // (/agent-insights/members/<uuid>), so gate on admin too: the pill is an
  // org-wide roster shortcut, not something a member's own page needs.
  const isAgentInsights =
    pathname?.startsWith('/agent-insights') && isAdminOrOwner(user?.Role);
  const chatSidebarWidth = 'var(--chat-sidebar-width)';
  const headerDividerOffset = '1px';
  const collapsedTopWidth = '64px';
  const { isCollapsed: isChatCollapsed, setIsCollapsed: setChatCollapsed } =
    useChatSidebarCollapsed();

  const isOpen = useAskEllieSidebarStore((s) => s.isOpen);
  const openSidebar = useAskEllieSidebarStore((s) => s.openSidebar);
  const hasMounted = useHasMounted();

  const showAskEllieButton = hasMounted && !isOpen && !isAIAssistantPage;

  return (
    <div
      className={cn(
        // No surface of its own — the shell's pixel-field texture runs behind
        // the header strip too.
        'text-ds-text-default transition-colors relative',
        isAIAssistantPage && 'ai-assistant-header'
      )}
    >
      {isAIAssistantPage && (
        <>
          <span
            className="absolute top-0 bottom-0 w-px bg-ds-border-subtlest pointer-events-none"
            style={{
              left: `calc(${isChatCollapsed ? collapsedTopWidth : chatSidebarWidth} + ${headerDividerOffset} - 1px)`,
            }}
          />
          <div
            className="absolute inset-y-0 left-0 flex items-center justify-between px-4 border-r border-ds-border-subtlest bg-ds-surface-off-white"
            style={{
              width: isChatCollapsed
                ? collapsedTopWidth
                : `calc(${chatSidebarWidth} + ${headerDividerOffset})`,
            }}
          >
            {!isChatCollapsed && (
              <span className="text-base font-medium text-ds-text-darker">
                Ask Ellie
              </span>
            )}
            <button
              onClick={() => setChatCollapsed(!isChatCollapsed)}
              aria-label={
                isChatCollapsed
                  ? 'Expand chat sidebar'
                  : 'Collapse chat sidebar'
              }
              className={cn(
                'p-2 rounded-md hover:bg-primitive-gray-100 text-ds-text-subtle transition-opacity',
                isChatCollapsed ? 'opacity-100' : 'opacity-60 hover:opacity-100'
              )}
            >
              {isChatCollapsed ? (
                <PanelRight size={16} />
              ) : (
                <PanelLeft size={16} />
              )}
            </button>
          </div>
        </>
      )}
      {/* Main Header */}
      <div
        className={cn('flex h-[3.45rem] items-center justify-between px-6')}
        style={
          isAIAssistantPage
            ? {
                paddingLeft: `calc(1.5rem + ${isChatCollapsed ? collapsedTopWidth : chatSidebarWidth})`,
              }
            : undefined
        }
      >
        {/* Breadcrumbs */}
        <Breadcrumbs className="text-sm text-ds-text-subtle" showHome={false} />

        {/* Search */}
        {/* <div className="flex-1 max-w-md mx-8">
          <div className="relative">
            <Search
              className={`
                absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-subtler
              `}
            />
            <Input
              placeholder="Search..."
              className={` pl-9 bg-primitive-gray-100 border border-ds-border-subtlest text-ds-text-darker placeholder:text-ds-text-subtler focus:bg-ds-surface-white transition-colors`}
            />
          </div>
        </div> */}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            ref={themeBtnRef}
            variant="ghost"
            size="sm"
            onClick={() => toggleTheme()}
            className="hover:bg-primitive-gray-100"
            aria-label="Toggle theme"
          >
            {!hasMounted ? (
              <Moon className="h-4 w-4 text-ds-text-subtle" />
            ) : resolvedTheme === 'dark' ? (
              <Sun className="h-4 w-4 text-ds-text-subtle" />
            ) : (
              <Moon className="h-4 w-4 text-ds-text-subtle" />
            )}
          </Button>

          {/* <Button
            variant="ghost"
            size="sm"
            className="relative hover:bg-primitive-gray-100 "
          >
            <Bell className="h-4 w-4 text-ds-text-subtle" />
            <Badge
              className={` absolute -top-1 -right-1 h-4 w-4 p-0 text-xs bg-ds-brand-subtler text-ds-text-darker text-center justify-center items-center`}
            >
              3
            </Badge>
          </Button>      */}

          {isAgentInsights && <MembersActivePill />}

          {showAskEllieButton && (
            <button
              onClick={openSidebar}
              className="flex items-center justify-center rounded-md border-[1.5px] border-ds-border-subtlest bg-ds-surface-white px-2.5 py-2 ds-text-body-sm-medium text-ds-text-default hover:bg-ds-gray-subtlest transition-colors"
            >
              <Image
                src="/assets/ellie.png"
                alt=""
                width={20}
                height={20}
                className="size-5 shrink-0 object-cover rounded-full"
              />
              <span className="px-2">Ask Ellie</span>
            </button>
          )}
        </div>
      </div>

      {/* Document Header */}
      {showDocumentActions && (
        <div
          className={`
            flex h-12 items-center justify-between px-6 border-t bg-primitive-gray-100
            border-ds-border-subtlest
          `}
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 hover:bg-ds-border-subtler"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <span className="font-medium">{documentName}</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 hover:bg-ds-border-subtler"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 hover:bg-ds-border-subtler"
            >
              <Share className="h-4 w-4" />
              Share
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 hover:bg-ds-border-subtler"
                  >
                    <Download className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                }
              />
              <DropdownMenuContent align="end">
                <DropdownMenuItem>Export as PDF</DropdownMenuItem>
                <DropdownMenuItem>Export as PNG</DropdownMenuItem>
                <DropdownMenuItem>Export as SVG</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
}
